import { ForwardedRef, useImperativeHandle, useRef, useState } from 'react';
import YouTube from 'react-youtube';
import { ValuesType } from 'utility-types';

import { seconds } from '~/interfaces';
import { VideoState } from '~/modules/elements/video-player/video-state';
import usePlayerVolume from '~/modules/hooks/use-player-volume';
import useUnstuckYouTubePlayer from '~/modules/hooks/use-unstuck-you-tube-player';

const stateMap = {
  [YouTube.PlayerState.UNSTARTED]: VideoState.UNSTARTED,
  [YouTube.PlayerState.ENDED]: VideoState.ENDED,
  [YouTube.PlayerState.PLAYING]: VideoState.PLAYING,
  [YouTube.PlayerState.PAUSED]: VideoState.PAUSED,
  [YouTube.PlayerState.BUFFERING]: VideoState.BUFFERING,
  [YouTube.PlayerState.CUED]: VideoState.CUED,
} as const;

interface Props {
  video: string;
  autoplay?: boolean;
  controls?: boolean;
  disablekb?: boolean;
  volume?: number;
  startAt?: number;
  onStateChange?: (state: VideoState) => void;
  /** The player finished its API handshake. Commands sent before this (loadVideoById, playVideo…)
   * are posted to an iframe that isn't listening yet and are dropped, silently and for good. */
  onReady?: () => void;
  width: number;
  height: number;
  ref?: ForwardedRef<VideoPlayerRef>;
}

export interface LoadVideByIdOpts {
  videoId: string;
  startSeconds: number;
  endSeconds: number;
}

export interface VideoPlayerRef {
  getStatus: () => ValuesType<typeof stateMap>;
  seekTo: (time: seconds) => void;
  setPlaybackSpeed: (speed: number) => void;
  setVolume: (newVolume: number) => void;
  getCurrentTime: () => Promise<seconds>;
  loadVideoById: (opts: LoadVideByIdOpts) => void;
  setSize: (w: number, h: number) => void;
  playVideo: () => void;
  pauseVideo: () => void;
  getDuration: () => Promise<number>;
}

export default function YoutubeVideoPlayer({
  video,
  autoplay,
  startAt,
  controls,
  disablekb,
  volume,
  width,
  height,
  onStateChange,
  onReady,
  ref,
}: Props) {
  const player = useRef<YouTube | null>(null);
  const [currentStatus, setCurrentStatus] = useState(YouTube.PlayerState.UNSTARTED);

  const playerKey = useUnstuckYouTubePlayer(player, currentStatus);
  usePlayerVolume(player, volume);

  // There is no player var that forces captions off: `cc_load_policy: 0` only declines to force
  // them on, so a viewer whose YouTube account defaults to captions still gets them burned over the
  // song.
  //
  // `unloadModule` is the usual advice for this and cannot work here. `react-youtube` hands out a
  // `youtube-player` proxy rather than the raw player, and that proxy forwards only the function
  // names on its own whitelist - `unloadModule` is not one of them, so the call was `undefined`,
  // threw, and was swallowed by the catch around it. `setOption` IS forwarded, and clearing the
  // track is what actually takes the captions off.
  //
  // Both names, because the HTML5 player calls it `captions` while `cc` is the one that still
  // answers on some videos. The proxy is promise-based, so a name the player does not have rejects
  // rather than throwing, and has to be caught off the promise.
  //
  // Only YouTube's own caption track. Lyrics that are part of the video image cannot be removed.
  const turnCaptionsOff = () => {
    const internal = player.current?.getInternalPlayer();

    for (const module of ['captions', 'cc']) {
      try {
        void Promise.resolve(internal?.setOption(module, 'track', {})).catch(() => {
          // A player exposes one name or the other; the one that is not there is expected to fail.
        });
      } catch {
        // Same again for a proxy that refuses synchronously.
      }
    }
  };

  useImperativeHandle(ref, () => ({
    getStatus: () => stateMap[currentStatus],
    setSize: (w, h) => player.current?.getInternalPlayer()!.setSize(w, h),
    seekTo: (timeSec: seconds) => player.current?.getInternalPlayer()!.seekTo(timeSec, true),
    setPlaybackSpeed: (speed: number) => player.current?.getInternalPlayer()!.setPlaybackRate(Number(speed)),
    setVolume: (newVolume: number) => player.current?.getInternalPlayer()!.setVolume(newVolume),
    getCurrentTime: () => player.current?.getInternalPlayer?.()?.getCurrentTime?.() ?? Promise.resolve(0),
    loadVideoById: (opts) => player.current?.getInternalPlayer()!.loadVideoById(opts),
    playVideo: () => {
      // console.log('play');
      player.current?.getInternalPlayer()!.playVideo();
    },
    pauseVideo: () => {
      // console.log('paused');
      return player.current?.getInternalPlayer()!.pauseVideo();
    },
    getDuration: () => player.current?.getInternalPlayer?.()?.getDuration?.() ?? Promise.resolve(0),
  }));

  return (
    <YouTube
      title=" "
      key={`${video}-${playerKey}`}
      ref={player}
      videoId={video}
      opts={{
        width: width,
        height: height,
        playerVars: {
          autoplay: autoplay ? 1 : 0,
          cc_load_policy: 0,
          rel: 0,
          fs: 0,
          controls: controls ? 1 : 0,
          start: startAt ?? 0,
          end: 0,
          disablekb: disablekb ? 1 : 0,
          modestbranding: 1,
        },
      }}
      onReady={() => {
        // The captions module is fetched asynchronously and often arrives after playback has begun,
        // so clearing the track only on ready and on play races it and loses. `apiChange` is the
        // event the player fires when it loads or unloads a module - i.e. exactly when captions
        // turn up - but `react-youtube` does not forward it, so it is subscribed on the underlying
        // player. No unsubscribe: the emitter belongs to the player and goes when it is destroyed.
        const internal = player.current?.getInternalPlayer() as { on?: (event: string, handler: () => void) => void };
        internal?.on?.('apiChange', turnCaptionsOff);

        turnCaptionsOff();
        onReady?.();
      }}
      onPlaybackRateChange={(e) => {
        console.log('onPlaybackRateChange', e.data);
      }}
      onStateChange={(e) => {
        if (e.data === YouTube.PlayerState.PLAYING) {
          turnCaptionsOff();
        }
        setCurrentStatus(e.data);
        onStateChange?.(stateMap[e.data]);
      }}
    />
  );
}
